import { useState } from "react";

import { CallsService } from "../services/calls.service";
import { TranscriptSegment } from "../types/calls.types";

// COMPLETE transcriptions don't change, so keep them for the session -
// anything else (not found / still processing) is re-fetched next time.
const TRANSCRIPT_CACHE = new Map<string, TranscriptSegment[]>();

export const clearCallTranscriptionCache = () => {
  TRANSCRIPT_CACHE.clear();
};

// The API groups segments by speaker rather than chronologically
const sortSegments = (segments: TranscriptSegment[]) =>
  [...segments].sort((a, b) => a.start - b.start);

export const useCallTranscription = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set when there's no usable transcription (404 or not COMPLETE) - shown
  // as an info message rather than an error.
  const [unavailableReason, setUnavailableReason] = useState<string | null>(null);
  const [segments, setSegments] = useState<TranscriptSegment[] | null>(null);

  const loadTranscription = async (callId: string) => {
    const cached = TRANSCRIPT_CACHE.get(callId);
    if (cached) {
      setSegments(cached);
      return;
    }

    setLoading(true);
    setError(null);
    setUnavailableReason(null);
    try {
      const res = await CallsService.getTranscription(callId);
      if (!res) {
        setUnavailableReason("No transcription is available for this call.");
      } else if (res.transcription_status !== "COMPLETE" || !res.transcription) {
        setUnavailableReason(
          `Transcription not available yet (${res.transcription_status ?? "NOT QUEUED"}).`,
        );
      } else {
        const sorted = sortSegments(res.transcription);
        TRANSCRIPT_CACHE.set(callId, sorted);
        setSegments(sorted);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not retrieve transcription.");
    } finally {
      setLoading(false);
    }
  };

  return { loadTranscription, loading, error, unavailableReason, segments };
};
